/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, FormsModule } from '@angular/forms';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { InitStep } from '../../distribution/models/init-step';
import { SystemInitStep, SystemInitStepEntity } from '../../distribution/models/system-init-step';
import { InitializeService } from '../../distribution/services/initialize.service';
import { DataFieldGroup } from '../../shared/datafields/models';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { DeveloperService } from '../services/developer.service';
import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';

@Component({
  selector: 'app-developer-system-init-step-config',
  templateUrl: './developer-system-init-step-config.component.html',
  styleUrls: ['./developer-system-init-step-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, DataFieldGroupContainerComponent]
})
export class DeveloperSystemInitStepConfigComponent implements OnInit {
  constructor(
    private developerService: DeveloperService,
    private initStepService: InitializeService,
    private toastService: ToastService,
    private loadingPanelService: LoadingPanelService) {
  }

  disassembleDisplayGroups: DataFieldGroup[] = [];
  disassembleFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  initSteps: InitStep[] = [];

  @Input() systemInitStepType: SystemInitStep;
  currentSystemInitStep: SystemInitStepEntity;
  isEditing: boolean = false;

  ngOnInit() {
    void this.refresh();
  }

  enterEditMode() {
    this.disassembleFormGroup.enable();
    this.isEditing = true;
  }

  exitEditMode() {
    this.disassembleFormGroup.disable();
    this.isEditing = false;
  }

  async cancelEditMode() {
    this.exitEditMode();
    await this.refresh();
  }

  newSystemInitStep() {
    this.currentSystemInitStep = {
      Id: null,
      InitStepId: null,
      Type: this.systemInitStepType,
      Configuration: '',
      CreatedBy: -1, CreatedDate: new Date(), UpdatedBy: null, UpdatedDate: null
    };
    this.disassembleDisplayGroups = [];
  }

  async setSystemStep(initStepId: number) {
    this.currentSystemInitStep.InitStepId = initStepId;
    if (initStepId) {
      this.disassembleDisplayGroups = await this.developerService.getSystemInitStepDataFieldGroups(initStepId, this.currentSystemInitStep?.Id); // TODO: Skift til at kalde korrekt backend...
    }
    else {
      this.disassembleDisplayGroups = [];
    }
  }

  async refresh() {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading...');
      this.initSteps = (await this.initStepService.getInitSteps()).sort((a, b) => a.Name.localeCompare(b.Name));
      this.currentSystemInitStep = await this.developerService.getSystemInitStep(this.systemInitStepType);
      if (!this.currentSystemInitStep) {
        this.newSystemInitStep();
      }
      else {
        await this.setSystemStep(this.currentSystemInitStep.InitStepId);
      }
    }
    catch {
      this.toastService.showToast('Failed to load data...', ToastType.error);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  async save() {
    if (!this.currentSystemInitStep.InitStepId) {
      if (this.currentSystemInitStep.Id) {
        this.loadingPanelService.showLoadingPanelDirect('Deleting...');
        await this.developerService.deleteSystemInitStep(this.currentSystemInitStep.Id);
        this.loadingPanelService.hideLoadingPanel();
        this.newSystemInitStep();
        this.toastService.showToast('System init step deleted...', ToastType.info);
      }
    }
    else {
      if (this.disassembleDisplayGroups.length > 0) {
        this.disassembleFormGroup.updateValueAndValidity();
        if (!this.disassembleFormGroup.valid) {
          this.toastService.showToast('Please fill out all required fields...', ToastType.warning);
          return;
        }
      }

      this.loadingPanelService.showLoadingPanelDirect('Saving...');

      const datafieldValues = DataFieldGroup.getDataFieldValues(this.disassembleDisplayGroups, false);
      this.currentSystemInitStep = await this.developerService.saveSystemInitstep(this.currentSystemInitStep, datafieldValues);

      this.loadingPanelService.hideLoadingPanel();
      this.toastService.showToast('System init step saved...', ToastType.success);
    }

    this.exitEditMode();
  }
}
